import { useEffect, useMemo, useRef, useState } from "react";
import "../../../V2SideDrawer.css";
import "../styles/UserCreateDrawer.css";

const ROLE_OPTIONS = ["Super Admin", "Admin", "AM", "Project Lead", "Developer", "Guest", "External"];
const USER_TYPE_OPTIONS = ["Internal", "External", "Guest", "Admin"];

function MultiSelectDropdown({
  label,
  options,
  selectedIds,
  onChange,
  allLabel,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const allIds = useMemo(() => (options || []).map((o) => o.id), [options]);
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectedOptions = useMemo(
    () => (options || []).filter((opt) => selectedSet.has(opt.id)),
    [options, selectedSet]
  );
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));

  const toggleOne = (id) => {
    const next = selectedSet.has(id)
      ? (selectedIds || []).filter((x) => x !== id)
      : [...(selectedIds || []), id];
    onChange(next);
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : allIds);
  };

  return (
    <div className="userc-field" ref={ref}>
      <label className="v2drw-form-label">{label}</label>
      <button type="button" className="userc-multi-trigger v2drw-control" onClick={() => setOpen((v) => !v)}>
        {selectedOptions.length === 0 ? (
          <span className="userc-multi-placeholder">{placeholder}</span>
        ) : (
          <span className="userc-multi-summary">{selectedOptions.length} selected</span>
        )}
        <span className="userc-multi-caret">▾</span>
      </button>
      {selectedOptions.length > 0 && (
        <div className="userc-multi-selected">
          {selectedOptions.map((opt) => (
            <span key={opt.id} className="userc-multi-pill">
              <span className="userc-multi-pill-label">{opt.label}</span>
              <span
                className="userc-multi-pill-remove"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleOne(opt.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleOne(opt.id);
                  }
                }}
              >
                ×
              </span>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="userc-multi-drop">
          <label className="userc-multi-opt userc-multi-opt--all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            {allLabel}
          </label>
          {(options || []).map((opt) => (
            <label key={opt.id} className="userc-multi-opt">
              <input
                type="checkbox"
                checked={selectedSet.has(opt.id)}
                onChange={() => toggleOne(opt.id)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const SIGN_IN_METHODS = [
  { value: "microsoft", label: "Microsoft", desc: "Invited as a guest; signs in with their Microsoft account." },
  { value: "email", label: "Email", desc: "Email + password + authenticator app. No Microsoft account needed." },
];

export default function UserCreateDrawer({
  open,
  onClose,
  onCreate,
  creating = false,
  users = [],
  teams = [],
  customers = [],
  projects = [],
  actorRole = "",
  customerGroups = [],
  loadingCustomerGroups = false,
  onLoadCustomerGroups,
}) {
  const isSuperAdmin = String(actorRole || "").trim().toLowerCase() === "super admin";
  const roleOptions = ROLE_OPTIONS.filter((r) => r !== "Super Admin" || isSuperAdmin);
  const [form, setForm] = useState({
    display_name: "",
    user_id: "",
    user_name: "",
    mobile_no: "",
    user_type: "Internal",
    role: "Developer",
    reporting_manager_id: "",
    team_id: "",
    customer_ids: [],
    project_ids: [],
    sign_in_method: "microsoft",
    invite_group_id: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      display_name: "",
      user_id: "",
      user_name: "",
      mobile_no: "",
      user_type: "Internal",
      role: "Developer",
      reporting_manager_id: "",
      team_id: "",
      customer_ids: [],
      project_ids: [],
      sign_in_method: "microsoft",
      invite_group_id: "",
    });
    setErrors({});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key === "Escape" && !creating) onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, creating, onClose]);

  const managerOptions = users.map((u) => ({ id: u.id, label: u.display_name || u.user_name || "Unnamed" }));
  const teamOptions = teams.map((t) => ({ id: t.id, label: t.team_name || "Unnamed Team" }));
  const clientTeamOption = useMemo(
    () => teamOptions.find((t) => String(t.label || "").trim().toLowerCase() === "client team") || null,
    [teamOptions]
  );
  const customerOptions = customers.map((c) => ({ id: c.id, label: c.customer_name || "Unnamed Customer" }));
  const projectOptions = projects.map((p) => ({
    id: p.id,
    label: p.customer_name ? `${p.project_name} (${p.customer_name})` : (p.project_name || "Unnamed Project"),
  }));
  const normalizedUserType = String(form.user_type || "").trim().toLowerCase();
  const lockRoleAndTeam = normalizedUserType === "external" || normalizedUserType === "admin";

  useEffect(() => {
    if (!open || !lockRoleAndTeam) return;
    const targetTeamId = clientTeamOption?.id || "";
    setForm((prev) => {
      if (prev.role === "External" && prev.team_id === targetTeamId) return prev;
      return { ...prev, role: "External", team_id: targetTeamId };
    });
  }, [open, lockRoleAndTeam, clientTeamOption]);

  const needsGuestGroup = open && normalizedUserType === "external" && form.sign_in_method === "microsoft";
  useEffect(() => {
    if (needsGuestGroup && customerGroups.length === 0 && !loadingCustomerGroups) onLoadCustomerGroups?.();
  }, [needsGuestGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.display_name.trim()) next.display_name = "Required";
    if (!form.user_type) next.user_type = "Required";
    if (!form.role) next.role = "Required";
    if (!form.reporting_manager_id) next.reporting_manager_id = "Required";
    if (!form.team_id) next.team_id = "Required";
    if (lockRoleAndTeam && !clientTeamOption?.id) next.team_id = "Team 'Client Team' is not configured";
    if (normalizedUserType === "external") {
      // The invitation is sent to this address, so it must be a real email.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_name.trim())) next.user_name = "A valid email is required to send the invitation";
      if (form.sign_in_method === "microsoft" && !form.invite_group_id) next.invite_group_id = "Required for the Microsoft invitation";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreate = () => {
    if (!validate()) return;
    onCreate?.({
      display_name: form.display_name.trim(),
      user_id: (form.user_id || "").replace(/[^a-z0-9]/gi, "").slice(0, 10),
      user_name: form.user_name.trim(),
      mobile_no: form.mobile_no.trim(),
      user_type: form.user_type,
      role: lockRoleAndTeam ? "External" : form.role,
      reporting_manager_id: form.reporting_manager_id,
      team_id: lockRoleAndTeam ? (clientTeamOption?.id || "") : form.team_id,
      customer_ids: form.customer_ids,
      project_ids: form.project_ids,
      is_active: true,
      email_login_enabled: normalizedUserType === "external" && form.sign_in_method === "email",
      // Which invitation to send right after creating (External users only).
      invite: normalizedUserType === "external"
        ? (form.sign_in_method === "email"
            ? { method: "email" }
            : {
                method: "microsoft",
                groupId: form.invite_group_id,
                groupName: customerGroups.find((g) => g.id === form.invite_group_id)?.displayName || "",
              })
        : null,
    });
  };

  return (
    <>
      <div className="v2drw-scrim" onClick={() => !creating && onClose?.()} />
      <div className="v2drw userc-drawer" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-eyebrow">Users</div>
            <div className="v2drw-title">New User</div>
          </div>
          <button className="v2drw-close" onClick={() => !creating && onClose?.()} aria-label="Close">✕</button>
        </div>

        <div className="v2drw-body">
          <div className="userc-grid">
            <div className="userc-field">
              <label className="v2drw-form-label">Name *</label>
              <input
                className={`v2drw-control${errors.display_name ? " userc-err" : ""}`}
                value={form.display_name}
                onChange={(e) => setField("display_name", e.target.value)}
                autoFocus
              />
              {errors.display_name && <span className="userc-err-text">{errors.display_name}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">User ID</label>
              <input
                className="v2drw-control"
                value={form.user_id}
                maxLength={10}
                onChange={(e) => setField("user_id", (e.target.value || "").replace(/[^a-z0-9]/gi, "").slice(0, 10))}
              />
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Email / Username{normalizedUserType === "external" ? " *" : ""}</label>
              <input
                className={`v2drw-control${errors.user_name ? " userc-err" : ""}`}
                value={form.user_name}
                onChange={(e) => setField("user_name", e.target.value)}
              />
              {errors.user_name && <span className="userc-err-text">{errors.user_name}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Mobile</label>
              <input
                className="v2drw-control"
                value={form.mobile_no}
                onChange={(e) => setField("mobile_no", e.target.value)}
              />
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">User Type *</label>
              <select
                className={`v2drw-control${errors.user_type ? " userc-err" : ""}`}
                value={form.user_type}
                onChange={(e) => setField("user_type", e.target.value)}
              >
                <option value="">Select user type</option>
                {USER_TYPE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {errors.user_type && <span className="userc-err-text">{errors.user_type}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Role *</label>
              <select
                className={`v2drw-control${errors.role ? " userc-err" : ""}`}
                value={form.role}
                disabled={lockRoleAndTeam}
                onChange={(e) => setField("role", e.target.value)}
              >
                <option value="">Select role</option>
                {roleOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {errors.role && <span className="userc-err-text">{errors.role}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Reporting Manager *</label>
              <select
                className={`v2drw-control${errors.reporting_manager_id ? " userc-err" : ""}`}
                value={form.reporting_manager_id}
                onChange={(e) => setField("reporting_manager_id", e.target.value)}
              >
                <option value="">Select reporting manager</option>
                {managerOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              {errors.reporting_manager_id && <span className="userc-err-text">{errors.reporting_manager_id}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Team *</label>
              <select
                className={`v2drw-control${errors.team_id ? " userc-err" : ""}`}
                value={form.team_id}
                disabled={lockRoleAndTeam}
                onChange={(e) => setField("team_id", e.target.value)}
              >
                <option value="">Select team</option>
                {teamOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
              {errors.team_id && <span className="userc-err-text">{errors.team_id}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Status</label>
              <input className="v2drw-control" value="Active" readOnly />
            </div>

            {normalizedUserType === "external" && (
              <div className="userc-field userc-span-2 userc-signin">
                <label className="v2drw-form-label">Sign-in method *</label>
                <div className="userc-signin__options" role="radiogroup">
                  {SIGN_IN_METHODS.map((m) => (
                    <label key={m.value} className={`userc-signin__opt${form.sign_in_method === m.value ? " is-on" : ""}`}>
                      <input
                        type="radio"
                        name="sign_in_method"
                        value={m.value}
                        checked={form.sign_in_method === m.value}
                        onChange={() => setField("sign_in_method", m.value)}
                      />
                      <span className="userc-signin__title">{m.label}</span>
                      <span className="userc-signin__desc">{m.desc}</span>
                    </label>
                  ))}
                </div>
                {form.sign_in_method === "microsoft" ? (
                  <div className="userc-signin__extra">
                    <label className="v2drw-form-label">Customer group (Microsoft invitation) *</label>
                    <select
                      className={`v2drw-control${errors.invite_group_id ? " userc-err" : ""}`}
                      value={form.invite_group_id}
                      onChange={(e) => setField("invite_group_id", e.target.value)}
                      disabled={loadingCustomerGroups}
                    >
                      <option value="">{loadingCustomerGroups ? "Loading groups…" : "— Select group —"}</option>
                      {customerGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.displayName || g.id}</option>
                      ))}
                    </select>
                    {errors.invite_group_id && <span className="userc-err-text">{errors.invite_group_id}</span>}
                  </div>
                ) : (
                  <span className="userc-signin__note">An email with a one-time setup link is sent when you create the user.</span>
                )}
              </div>
            )}

            {normalizedUserType === "external" ? (
              <div className="userc-field">
                <label className="v2drw-form-label">Customer</label>
                <select
                  className="v2drw-control"
                  value={form.customer_ids[0] || ""}
                  onChange={(e) => setField("customer_ids", e.target.value ? [e.target.value] : [])}
                >
                  <option value="">— None —</option>
                  {customerOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ) : (
              <MultiSelectDropdown
                label="Customers"
                options={customerOptions}
                selectedIds={form.customer_ids}
                onChange={(ids) => setField("customer_ids", ids)}
                allLabel="All Customers"
                placeholder="Select customers..."
              />
            )}

            <MultiSelectDropdown
              label="Projects"
              options={projectOptions}
              selectedIds={form.project_ids}
              onChange={(ids) => setField("project_ids", ids)}
              allLabel="All Projects"
              placeholder="Select projects..."
            />
          </div>
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={creating}>Cancel</button>
          <button className="v2drw-btn-save" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating..." : "Create User"}
          </button>
        </div>
      </div>
    </>
  );
}
